import { useEffect, useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { AvailableModules } from 'generated/featureDefinitions'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import BaseModal from 'modules/upsell/components/SelfUpsell/UpgradePlanModal/BaseModal'
import ErrorStep from 'modules/upsell/components/SelfUpsell/UpgradePlanModal/ErrorStep'
import FirstStep from 'modules/upsell/components/SelfUpsell/UpgradePlanModal/FirstStep'
import SecondStep from 'modules/upsell/components/SelfUpsell/UpgradePlanModal/SecondStep'
import SuccessStep from 'modules/upsell/components/SelfUpsell/UpgradePlanModal/SuccessStep'
import SurveyStep from 'modules/upsell/components/SelfUpsell/UpgradePlanModal/SurveyStep'
import UpsellCreatedStep from 'modules/upsell/components/SelfUpsell/UpgradePlanModal/UpsellCreatedStep'
import useCreateUpsellRequest, { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'
import { readLatestUpsellOrigin } from 'modules/upselling_kit/utils/localStorage'

type UpgradePlanModalProps = {
  isOpen: boolean
  onClose: () => void
  moduleName: AvailableModules
  from: string
  installedAddonInternalId: string | null
  hubspotModuleName?: string | null
}

const UpgradePlanModal = ({
  isOpen,
  onClose,
  moduleName,
  from,
  installedAddonInternalId,
  hubspotModuleName,
}: UpgradePlanModalProps) => {
  const [step, setStep] = useState('first')
  const { allAddons, shouldBypassSelfUpsell } = useSelfUpsellContext()
  const [selectedAddonId, setSelectedAddonId] = useState(
    allAddons.f25Addons[0]?.internalTitle || ''
  )

  const addon = useMemo(() => {
    return (
      allAddons.f25Addons.find((a) => a.internalTitle === selectedAddonId) || allAddons.f25Addons[0]
    )
  }, [allAddons.f25Addons, selectedAddonId])

  const { create } = useCreateUpsellRequest()

  const handleRequestUpsell = () => {
    if (shouldBypassSelfUpsell) return
    const latestUpsellOrigin = readLatestUpsellOrigin()
    const upsellOrigin = latestUpsellOrigin?.request_source
    const requestSourceCategory = latestUpsellOrigin?.request_source_category || 'Organic'
    const requestSourceComponent = latestUpsellOrigin?.request_source_component

    const abandonedCartRequestSource = upsellOrigin || 'Marketplace'
    create({
      requestSource: abandonedCartRequestSource,
      requestSourceCategory,
      requestSourceComponent,
      moduleRequested: moduleName,
      requestReason: ValidReasons.MissingAddon,
      upsellRequestStatus: 'Opportunity detected',
      upsellRequestComponentType: 'self_upsell_modal',
      upsellRequestType: 'Abandoned cart',
      hubspotModuleName: hubspotModuleName || undefined,
    })
  }

  const handleClose = () => {
    track(trackingEvents.upsell.closeSelfUpsellModal, {
      module: moduleName,
      step: step,
    })
    if (step === 'survey') {
      onClose()
      setStep('first')
      return
    }
    if (step === 'success') {
      onClose()
      window.location.reload()
      return
    }
    if (step !== 'success' && step !== 'upsell-created' && !shouldBypassSelfUpsell) {
      handleRequestUpsell()
      onClose()
      setStep('survey')
    } else {
      onClose()
      setStep('first')
    }
  }

  const handleAssistance = () => {
    track(trackingEvents.upsell.clickRequestAssistanceButton, {
      module: moduleName,
      step: step,
      component: 'self_upsell_upgrade_modal',
    })
    app.setFlash({
      type: 'correct',
      message: i18n.t('upselling_kit.upselling_button.success_message.title'),
    })
    handleClose()
  }

  useEffect(() => {
    if (isOpen) {
      track(trackingEvents.upsell.viewSelfUpsellUpgradeModal, {
        module: moduleName,
        from,
      })
    }
  }, [isOpen])

  if (!addon) return null

  return (
    <>
      <BaseModal isOpen={isOpen} onClose={handleClose} width="760px" isLocked>
        <Box
          style={{
            minHeight: '560px',
            maxHeight: '700px',
          }}
        >
          {step === 'first' && (
            <FirstStep
              addon={addon}
              step={step}
              setStep={setStep}
              setSelectedAddonId={setSelectedAddonId}
              installedAddonInternalId={installedAddonInternalId}
              handleAssistance={handleAssistance}
              moduleName={moduleName}
            />
          )}
          {step === 'second' && (
            <SecondStep
              addon={addon}
              step={step}
              setStep={setStep}
              moduleName={moduleName}
              installedAddonInternalId={installedAddonInternalId}
            />
          )}
          {step === 'success' && (
            <SuccessStep addon={addon} step={step} onClose={handleClose} moduleName={moduleName} />
          )}

          {step === 'upsell-created' && (
            <UpsellCreatedStep
              addon={addon}
              step={step}
              onClose={handleClose}
              moduleName={moduleName}
            />
          )}
          {step === 'error' && (
            <ErrorStep
              addon={addon}
              step={step}
              onClose={handleClose}
              handleAssistance={handleAssistance}
              moduleName={moduleName}
            />
          )}
        </Box>
      </BaseModal>

      {step === 'survey' && (
        <SurveyStep
          moduleName={moduleName}
          onClose={() => {
            setStep('first')
            onClose()
          }}
        />
      )}
    </>
  )
}

export default UpgradePlanModal
